import { useEffect, useMemo, useState } from "react";
import { api, formatEUR } from "../lib/apiClient";
import { useWaves } from "../context/WaveContext";
import { toast } from "sonner";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { ChevronDown, ChevronRight, Wallet, Download } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const fmtInt = (n) => Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 });

const emptyAgg = () => ({ anticipo: 0, restante: 0, total_90: 0, total_100: 0, sobre_coste: 0, count: 0 });

const sumInto = (agg, ev) => {
  agg.anticipo += ev.anticipo || 0;
  agg.restante += ev.restante || 0;
  agg.total_90 += ev.total_90 || 0;
  agg.total_100 += ev.total_100 || 0;
  agg.sobre_coste += ev.sobre_coste || 0;
  agg.count += 1;
};

export default function Cashout() {
  const { currentWaveId } = useWaves();
  const [lines, setLines] = useState([]);
  const [cityFilter, setCityFilter] = useState("all");
  const [restaurantFilter, setRestaurantFilter] = useState("all");
  const [includeProvisional, setIncludeProvisional] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    if (!currentWaveId) return;
    setLoading(true);
    try {
      const c = await api.get("/cashout", { params: { include_provisional: includeProvisional } });
      setLines(c.data.lines || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [currentWaveId, includeProvisional]); // eslint-disable-line react-hooks/exhaustive-deps

  const cities = useMemo(() => {
    const s = new Set(lines.map((l) => l.city || "Sin ciudad"));
    return Array.from(s).sort();
  }, [lines]);

  const filtered = useMemo(() => {
    return lines.filter((l) => {
      if (cityFilter !== "all" && (l.city || "Sin ciudad") !== cityFilter) return false;
      if (restaurantFilter !== "all" && l.restaurant_id !== restaurantFilter) return false;
      return true;
    });
  }, [lines, cityFilter, restaurantFilter]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((l) => {
      const key = l.restaurant_id;
      if (!map[key]) map[key] = { restaurant_id: key, restaurant_name: l.restaurant_name, city: l.city || "Sin ciudad", billing_scheme: l.billing_scheme, confirmed: [], provisional: [] };
      if (l.event_status === "provisional") map[key].provisional.push(l);
      else map[key].confirmed.push(l);
    });
    Object.values(map).forEach((g) => {
      g.confirmed.sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));
      g.provisional.sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));
      g.aggConf = emptyAgg();
      g.confirmed.forEach((e) => sumInto(g.aggConf, e));
      g.aggProv = emptyAgg();
      g.provisional.forEach((e) => sumInto(g.aggProv, e));
    });
    return Object.values(map).sort((a, b) => (a.city || "").localeCompare(b.city || "") || (a.restaurant_name || "").localeCompare(b.restaurant_name || ""));
  }, [filtered]);

  const grandConf = useMemo(() => {
    const a = emptyAgg();
    grouped.forEach((g) => {
      a.anticipo += g.aggConf.anticipo; a.restante += g.aggConf.restante;
      a.total_90 += g.aggConf.total_90; a.total_100 += g.aggConf.total_100;
      a.sobre_coste += g.aggConf.sobre_coste; a.count += g.aggConf.count;
    });
    return a;
  }, [grouped]);

  const grandProv = useMemo(() => {
    const a = emptyAgg();
    grouped.forEach((g) => {
      a.anticipo += g.aggProv.anticipo; a.restante += g.aggProv.restante;
      a.total_90 += g.aggProv.total_90; a.total_100 += g.aggProv.total_100;
      a.sobre_coste += g.aggProv.sobre_coste; a.count += g.aggProv.count;
    });
    return a;
  }, [grouped]);

  const uniqueRestaurantsForFilter = useMemo(() => {
    const seen = new Set();
    const arr = [];
    lines.forEach((l) => {
      if (cityFilter !== "all" && (l.city || "Sin ciudad") !== cityFilter) return;
      if (seen.has(l.restaurant_id)) return;
      seen.add(l.restaurant_id);
      arr.push({ id: l.restaurant_id, name: l.restaurant_name, city: l.city || "Sin ciudad" });
    });
    return arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [lines, cityFilter]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/cashout/export/excel", {
        params: { include_provisional: includeProvisional, city: cityFilter },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      link.setAttribute("download", match?.[1] || "cashout.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel de Cashout descargado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo generar el Excel");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="cashout-page">
      <div className="mb-6">
        <p className="label-eyebrow flex items-center gap-2"><Wallet className="h-3.5 w-3.5" strokeWidth={1.5} /> Previsión de pagos a restaurantes</p>
        <h1 className="font-heading text-4xl mt-1">Cashout</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-4xl">
          Dinero previsto a pagar a cada restaurante, con desglose por evento (aforo máx/mín, precio menú, anticipo, restante y extras non-show si se llegara al 100% del aforo).
          Fechas <strong>confirmadas</strong> y <strong>pendientes de confirmar</strong> se muestran por separado dentro de cada restaurante.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-end gap-4 mb-6 flex-wrap">
        <div className="min-w-[180px]">
          <Label className="text-xs uppercase tracking-wider">Ciudad</Label>
          <Select value={cityFilter} onValueChange={(v) => { setCityFilter(v); setRestaurantFilter("all"); }}>
            <SelectTrigger className="rounded-none" data-testid="cashout-city-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none max-h-80">
              <SelectItem value="all">Todas las ciudades</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[240px]">
          <Label className="text-xs uppercase tracking-wider">Restaurante</Label>
          <Select value={restaurantFilter} onValueChange={setRestaurantFilter}>
            <SelectTrigger className="rounded-none" data-testid="cashout-restaurant-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none max-h-80">
              <SelectItem value="all">Todos los restaurantes</SelectItem>
              {uniqueRestaurantsForFilter.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name}{r.city ? ` · ${r.city}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch id="include-prov" checked={includeProvisional} onCheckedChange={setIncludeProvisional} data-testid="cashout-toggle-provisional" />
          <Label htmlFor="include-prov" className="text-xs uppercase tracking-wider cursor-pointer">Incluir pendientes</Label>
        </div>
        <div className="ml-auto pb-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || loading || grouped.length === 0} data-testid="cashout-export-excel" className="rounded-none">
            <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
            {exporting ? "Generando…" : "Descargar Excel"}
          </Button>
        </div>
      </div>

      {/* Grand totals cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-emerald-50 border border-emerald-200 p-4" data-testid="cashout-grand-confirmed">
          <p className="label-eyebrow text-emerald-800">Confirmadas ({grandConf.count})</p>
          <p className="font-heading text-2xl mt-2 font-mono">{formatEUR(grandConf.total_90)}</p>
          <div className="mt-2 pt-2 border-t border-emerald-200 text-xs font-mono space-y-0.5">
            <p className="text-zinc-700">Anticipo (10%) <span className="float-right">{formatEUR(grandConf.anticipo)}</span></p>
            <p className="text-zinc-700">Restante (90%) <span className="float-right">{formatEUR(grandConf.restante)}</span></p>
            <p className="text-rose-700">Extras non-show (100%) <span className="float-right">{formatEUR(grandConf.sobre_coste)}</span></p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 p-4" data-testid="cashout-grand-pending">
          <p className="label-eyebrow text-amber-800">Pendientes ({grandProv.count})</p>
          <p className="font-heading text-2xl mt-2 font-mono">{formatEUR(grandProv.total_90)}</p>
          <div className="mt-2 pt-2 border-t border-amber-200 text-xs font-mono space-y-0.5">
            <p className="text-zinc-700">Anticipo (10%) <span className="float-right">{formatEUR(grandProv.anticipo)}</span></p>
            <p className="text-zinc-700">Restante (90%) <span className="float-right">{formatEUR(grandProv.restante)}</span></p>
            <p className="text-rose-700">Extras non-show (100%) <span className="float-right">{formatEUR(grandProv.sobre_coste)}</span></p>
          </div>
        </div>
        <div className="bg-zinc-900 text-white p-4" data-testid="cashout-grand-total">
          <p className="label-eyebrow text-zinc-400">Total al 90% (mín pactado)</p>
          <p className="font-heading text-2xl mt-2 font-mono">{formatEUR(grandConf.total_90 + grandProv.total_90)}</p>
          <p className="text-[10px] text-zinc-400 mt-1">Confirmadas + pendientes</p>
        </div>
        <div className="bg-rose-50 border border-rose-200 p-4" data-testid="cashout-grand-sobrecoste">
          <p className="label-eyebrow text-rose-800">Extras non-show al 100%</p>
          <p className="font-heading text-2xl mt-2 font-mono">{formatEUR(grandConf.sobre_coste + grandProv.sobre_coste)}</p>
          <p className="text-[10px] text-rose-700 mt-1">Si se llenase todo el aforo máximo</p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500 py-8">Cargando…</p>
      ) : grouped.length === 0 ? (
        <p className="text-sm text-zinc-500 py-8" data-testid="cashout-empty">No hay eventos en esta wave con los filtros aplicados.</p>
      ) : (
        <div className="space-y-3">
          {grouped.map((g) => {
            const isCollapsed = collapsed[g.restaurant_id] ?? false;
            const totalRest = g.aggConf.total_90 + g.aggProv.total_90;
            return (
              <div key={g.restaurant_id} className="bg-white border border-zinc-200" data-testid={`cashout-restaurant-${g.restaurant_id}`}>
                <button
                  onClick={() => setCollapsed({ ...collapsed, [g.restaurant_id]: !isCollapsed })}
                  className="w-full px-5 py-3 border-b border-zinc-200 flex items-center justify-between hover:bg-zinc-50 text-left flex-wrap gap-3"
                >
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    {isCollapsed ? <ChevronRight className="h-4 w-4 text-zinc-400" strokeWidth={1.5} /> : <ChevronDown className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />}
                    <p className="font-medium">{g.restaurant_name}</p>
                    <span className="text-xs text-zinc-500 font-mono px-2 py-0.5 bg-zinc-100 border border-zinc-200">{g.city}</span>
                    {g.billing_scheme === "amar_daily" && (
                      <span data-testid={`cashout-amar-badge-${g.restaurant_id}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-sky-100 text-sky-800 border border-sky-300 font-mono" title="Anticipo = parte del 10% ÷ nº de fechas · Restante = pago del día con asistencia real (o mínimo si no está cargada)">
                        Amar · pago diario
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs font-mono flex-wrap justify-end">
                    <span className="text-emerald-700">{g.aggConf.count} conf. · {formatEUR(g.aggConf.total_90)}</span>
                    {g.aggProv.count > 0 && (
                      <span className="text-amber-700">{g.aggProv.count} pend. · {formatEUR(g.aggProv.total_90)}</span>
                    )}
                    <span className="font-bold text-zinc-900">Total {formatEUR(totalRest)}</span>
                  </div>
                </button>
                {!isCollapsed && (
                  <div className="p-4 space-y-4">
                    {g.confirmed.length > 0 && (
                      <EventTable events={g.confirmed} agg={g.aggConf} label="CONFIRMADAS" tone="emerald" testid={`cashout-conf-${g.restaurant_id}`} />
                    )}
                    {g.provisional.length > 0 && (
                      <EventTable events={g.provisional} agg={g.aggProv} label="PENDIENTES DE CONFIRMAR" tone="amber" testid={`cashout-pend-${g.restaurant_id}`} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EventTable({ events, agg, label, tone, testid }) {
  const toneClasses = {
    emerald: { pill: "bg-emerald-100 text-emerald-800 border-emerald-300", border: "border-emerald-200", bgSub: "bg-emerald-50" },
    amber: { pill: "bg-amber-100 text-amber-800 border-amber-300", border: "border-amber-200", bgSub: "bg-amber-50" },
  }[tone];
  return (
    <div className={`border ${toneClasses.border}`} data-testid={testid}>
      <div className={`px-3 py-1.5 ${toneClasses.bgSub} flex items-center justify-between`}>
        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${toneClasses.pill} font-mono`}>{label}</span>
        <span className="text-[10px] text-zinc-600 font-mono">{events.length} eventos</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-600">
            <tr>
              <th className="text-left px-3 py-2">Fecha</th>
              <th className="text-left px-3 py-2">Día</th>
              <th className="text-center px-2 py-2">Cena</th>
              <th className="text-center px-2 py-2">Comida</th>
              <th className="text-right px-3 py-2">Aforo máx</th>
              <th className="text-right px-3 py-2">Aforo mín (90%)</th>
              <th className="text-right px-3 py-2">Precio menú</th>
              <th className="text-right px-3 py-2">Anticipo (10%)</th>
              <th className="text-right px-3 py-2">Restante (90%)</th>
              <th className="text-right px-3 py-2 bg-zinc-100">Total al 90%</th>
              <th className="text-right px-3 py-2">Total al 100%</th>
              <th className="text-right px-3 py-2 bg-rose-50">Extras non-show</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.event_id} className="border-t border-zinc-100" data-testid={`cashout-event-${ev.event_id}`}>
                <td className="px-3 py-2 font-mono">{formatDate(ev.event_date)}</td>
                <td className="px-3 py-2 text-zinc-600">{ev.day_of_week}</td>
                <td className="px-2 py-2 text-center text-zinc-700">{ev.meal_type === "dinner" ? "×" : ""}</td>
                <td className="px-2 py-2 text-center text-zinc-700">{ev.meal_type === "lunch" ? "×" : ""}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtInt(ev.pax_max)}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {fmtInt(ev.pax_min)}
                  {ev.pax_real != null && (
                    <div className="text-[10px] text-sky-700" data-testid={`cashout-real-${ev.event_id}`}>Real {fmtInt(ev.pax_real)}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono">{formatEUR(ev.price)}</td>
                <td className="px-3 py-2 text-right font-mono">{formatEUR(ev.anticipo)}</td>
                <td className="px-3 py-2 text-right font-mono">{formatEUR(ev.restante)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold bg-zinc-50">{formatEUR(ev.total_90)}</td>
                <td className="px-3 py-2 text-right font-mono">{formatEUR(ev.total_100)}</td>
                <td className="px-3 py-2 text-right font-mono text-rose-700 bg-rose-50/50">{formatEUR(ev.sobre_coste)}</td>
              </tr>
            ))}
            <tr className={`border-t-2 ${toneClasses.border} ${toneClasses.bgSub} font-semibold`}>
              <td className="px-3 py-2" colSpan={7}>Subtotal {label.toLowerCase()}</td>
              <td className="px-3 py-2 text-right font-mono">{formatEUR(agg.anticipo)}</td>
              <td className="px-3 py-2 text-right font-mono">{formatEUR(agg.restante)}</td>
              <td className="px-3 py-2 text-right font-mono bg-zinc-100">{formatEUR(agg.total_90)}</td>
              <td className="px-3 py-2 text-right font-mono">{formatEUR(agg.total_100)}</td>
              <td className="px-3 py-2 text-right font-mono text-rose-700 bg-rose-100">{formatEUR(agg.sobre_coste)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
